'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GfrCalculator() {
  const t = useTranslations("tools.gfrCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState<'adult' | 'child'>('adult');
  const [showResults, setShowResults] = useState(false);

  // Adult form state
  const [adultInputs, setAdultInputs] = useState({
    creatinine: 0.9,
    creatinineUnit: 'mgdL' as 'mgdL' | 'umolL',
    age: 50,
    gender: 'male' as 'male' | 'female',
    race: 'other' as 'black' | 'other',
  });

  // Child form state
  const [childInputs, setChildInputs] = useState({
    creatinine: 0.5,
    creatinineUnit: 'mgdL' as 'mgdL' | 'umolL',
    height: 110,
    heightUnit: 'cm' as 'cm' | 'inch',
  });

  // Results state
  const [results, setResults] = useState<{
    mainGfr: number;
    ckdEpi?: number;
    mdrd?: number;
    mayo?: number;
    schwartz?: number;
    stage: {
      title: string;
      description: string;
      class: string;
    };
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Calculation functions
  const calculateMDRD = (scr: number, age: number, gender: string, race: string): number => {
    let gfr = 175 * Math.pow(scr, -1.154) * Math.pow(age, -0.203);
    if (gender === 'female') gfr *= 0.742;
    if (race === 'black') gfr *= 1.212;
    return Math.round(gfr);
  };

  const calculateCKDEPI = (scr: number, age: number, gender: string, race: string): number => {
    let kappa: number, alpha: number, gfr: number;

    if (gender === 'female') {
      kappa = 0.7;
      alpha = scr <= 0.7 ? -0.329 : -1.209;
      gfr = 144 * Math.pow(Math.min(scr / kappa, 1), alpha) * Math.pow(Math.max(scr / kappa, 1), -1.209) * Math.pow(0.993, age);
    } else {
      kappa = 0.9;
      alpha = scr <= 0.9 ? -0.411 : -1.209;
      gfr = 141 * Math.pow(Math.min(scr / kappa, 1), alpha) * Math.pow(Math.max(scr / kappa, 1), -1.209) * Math.pow(0.993, age);
    }

    if (race === 'black') gfr *= 1.159;

    return Math.round(gfr);
  };

  const calculateMayo = (scr: number, age: number, gender: string): number => {
    if (scr < 0.8) scr = 0.8;

    let exponent = 1.911 + (5.249 / scr) - (2.114 / Math.pow(scr, 2)) - (0.00686 * age);
    if (gender === 'female') exponent -= 0.205;

    const gfr = Math.exp(exponent);
    return Math.round(gfr);
  };

  const calculateChildGFR = (scr: number, height: number): number => {
    return Math.round(0.413 * height / scr);
  };

  const getCKDStage = (gfr: number) => {
    if (gfr >= 90) {
      return {
        title: t('stageNormalTitle'),
        description: t('stageNormalDesc'),
        class: 'normal'
      };
    } else if (gfr >= 60) {
      return {
        title: t('stageMildTitle'),
        description: t('stageMildDesc'),
        class: 'normal'
      };
    } else if (gfr >= 45) {
      return {
        title: t('stageModerateATitle'),
        description: t('stageModerateADesc'),
        class: 'mild'
      };
    } else if (gfr >= 30) {
      return {
        title: t('stageModerateBTitle'),
        description: t('stageModerateBDesc'),
        class: 'moderate'
      };
    } else if (gfr >= 15) {
      return {
        title: t('stageSevereTitle'),
        description: t('stageSevereDesc'),
        class: 'severe'
      };
    } else {
      return {
        title: t('stageFailureTitle'),
        description: t('stageFailureDesc'),
        class: 'severe'
      };
    }
  };

  const handleAdultCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let creatinine = adultInputs.creatinine;
    if (adultInputs.creatinineUnit === 'umolL') {
      creatinine = creatinine / 88.42;
    }

    const mdrd = calculateMDRD(creatinine, adultInputs.age, adultInputs.gender, adultInputs.race);
    const ckdEpi = calculateCKDEPI(creatinine, adultInputs.age, adultInputs.gender, adultInputs.race);
    const mayo = calculateMayo(creatinine, adultInputs.age, adultInputs.gender);

    setResults({
      mainGfr: ckdEpi,
      ckdEpi,
      mdrd,
      mayo,
      stage: getCKDStage(ckdEpi),
    });

    setShowResults(true);
  };

  const handleChildCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let creatinine = childInputs.creatinine;
    if (childInputs.creatinineUnit === 'umolL') {
      creatinine = creatinine / 88.42;
    }

    let height = childInputs.height;
    if (childInputs.heightUnit === 'inch') {
      height = height * 2.54;
    }

    const gfr = calculateChildGFR(creatinine, height);

    setResults({
      mainGfr: gfr,
      schwartz: gfr,
      stage: getCKDStage(gfr),
    });

    setShowResults(true);
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#f59e0b',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = {
    textAlign: 'center' as const,
    marginBottom: '2rem',
  };

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center' as const },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const tabButtonStyle = (isActive: boolean) => ({
    padding: '0.75rem 1.5rem',
    border: isActive ? 'none' : `2px solid #e2e8f0`,
    background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
    borderRadius: '0.75rem',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    color: isActive ? 'white' : colors.textSecondary,
    boxShadow: isActive ? '0 4px 12px rgba(37, 99, 235, 0.3)' : 'none',
    width: screenSize === 'mobile' ? '100%' : 'auto',
  });

  const formCardStyle = {
    background: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    marginBottom: '2rem',
  };

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
  };

  const labelStyle = {
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
  };

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const selectStyle = {
    ...inputStyle,
    minWidth: '120px',
    flex: 'none' as const,
  };

  const radioGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1.5rem', marginTop: '0.5rem' },
    { flexDirection: 'column' as const, gap: '0.75rem' },
    { flexDirection: 'row' as const, gap: '1.5rem' },
    { flexDirection: 'row' as const, gap: '1.5rem' }
  );

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#475569',
  };

  const radioInputStyle = {
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer',
    accentColor: colors.primary,
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1.125rem', fontSize: '1.125rem' },
    { padding: '1.25rem', fontSize: '1.25rem' }
  );

  const resultsCardStyle = {
    background: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  };

  const resultMainStyle = {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem',
    borderLeft: `4px solid ${colors.primary}`,
  };

  const resultLabelStyle = {
    fontSize: '0.9rem',
    color: colors.textSecondary,
    fontWeight: 600,
    display: 'block',
    marginBottom: '0.5rem',
  };

  const resultValueStyle = getResponsiveStyle(
    { color: colors.primary, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const resultUnitStyle = {
    fontSize: '1rem',
    color: colors.textSecondary,
    marginLeft: '0.5rem',
  };

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultItemStyle = {
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
  };

  const ckdStageStyle = (stageClass: string) => {
    const baseStyle = {
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid',
    };

    const classStyles: { [key: string]: any } = {
      normal: {
        background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        borderLeftColor: '#10b981',
      },
      mild: {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderLeftColor: '#f59e0b',
      },
      moderate: {
        background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
        borderLeftColor: '#f97316',
      },
      severe: {
        background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
        borderLeftColor: '#ef4444',
      },
    };

    return { ...baseStyle, ...classStyles[stageClass] };
  };

  const infoBoxStyle = {
    background: '#f1f5f9',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #64748b',
    marginTop: '1rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(37, 99, 235, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = {
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
  };

  const referenceStyle = {
    ...paragraphStyle,
    fontSize: '0.95rem',
    marginBottom: '0.75rem',
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🩺 {t('toolTitle')}</h2>
            <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={tabsContainerStyle}>
            <button
              style={tabButtonStyle(activeTab === 'adult')}
              onClick={() => {
                setActiveTab('adult');
                setShowResults(false);
              }}
            >
              {t('adultTab')}
            </button>
            <button
              style={tabButtonStyle(activeTab === 'child')}
              onClick={() => {
                setActiveTab('child');
                setShowResults(false);
              }}
            >
              {t('childTab')}
            </button>
          </div>

          <div style={formCardStyle}>
            {activeTab === 'adult' ? (
              <form onSubmit={handleAdultCalculate}>
                <div style={formGridStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="adult-creatinine">
                      {t('serumCreatinine')}
                    </label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="adult-creatinine"
                        step="0.01"
                        min="0"
                        value={adultInputs.creatinine}
                        onChange={(e) =>
                          setAdultInputs({ ...adultInputs, creatinine: parseFloat(e.target.value) || 0 })
                        }
                        style={inputStyle}
                        required
                      />
                      <select
                        value={adultInputs.creatinineUnit}
                        onChange={(e) =>
                          setAdultInputs({ ...adultInputs, creatinineUnit: e.target.value as 'mgdL' | 'umolL' })
                        }
                        style={selectStyle}
                      >
                        <option value="mgdL">mg/dL</option>
                        <option value="umolL">µmol/L</option>
                      </select>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="adult-age">
                      {t('age')}
                    </label>
                    <input
                      type="number"
                      id="adult-age"
                      min="18"
                      max="120"
                      value={adultInputs.age}
                      onChange={(e) => setAdultInputs({ ...adultInputs, age: parseInt(e.target.value) || 0 })}
                      style={inputStyle}
                      required
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('gender')}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="adult-gender"
                        value="male"
                        checked={adultInputs.gender === 'male'}
                        onChange={(e) => setAdultInputs({ ...adultInputs, gender: e.target.value as 'male' | 'female' })}
                        style={radioInputStyle}
                      />
                      {t('male')}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="adult-gender"
                        value="female"
                        checked={adultInputs.gender === 'female'}
                        onChange={(e) => setAdultInputs({ ...adultInputs, gender: e.target.value as 'male' | 'female' })}
                        style={radioInputStyle}
                      />
                      {t('female')}
                    </label>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('race')}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="adult-race"
                        value="black"
                        checked={adultInputs.race === 'black'}
                        onChange={(e) => setAdultInputs({ ...adultInputs, race: e.target.value as 'black' | 'other' })}
                        style={radioInputStyle}
                      />
                      {t('black')}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="adult-race"
                        value="other"
                        checked={adultInputs.race === 'other'}
                        onChange={(e) => setAdultInputs({ ...adultInputs, race: e.target.value as 'black' | 'other' })}
                        style={radioInputStyle}
                      />
                      {t('other')}
                    </label>
                  </div>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleChildCalculate}>
                <div style={formGridStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="child-creatinine">
                      {t('serumCreatinine')}
                    </label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="child-creatinine"
                        step="0.01"
                        min="0"
                        value={childInputs.creatinine}
                        onChange={(e) =>
                          setChildInputs({ ...childInputs, creatinine: parseFloat(e.target.value) || 0 })
                        }
                        style={inputStyle}
                        required
                      />
                      <select
                        value={childInputs.creatinineUnit}
                        onChange={(e) =>
                          setChildInputs({ ...childInputs, creatinineUnit: e.target.value as 'mgdL' | 'umolL' })
                        }
                        style={selectStyle}
                      >
                        <option value="mgdL">mg/dL</option>
                        <option value="umolL">µmol/L</option>
                      </select>
                    </div>
                  </div>

                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="child-height">
                      {t('height')}
                    </label>
                    <div style={inputWrapperStyle}>
                      <input
                        type="number"
                        id="child-height"
                        step="0.1"
                        min="0"
                        value={childInputs.height}
                        onChange={(e) => setChildInputs({ ...childInputs, height: parseFloat(e.target.value) || 0 })}
                        style={inputStyle}
                        required
                      />
                      <select
                        value={childInputs.heightUnit}
                        onChange={(e) =>
                          setChildInputs({ ...childInputs, heightUnit: e.target.value as 'cm' | 'inch' })
                        }
                        style={selectStyle}
                      >
                        <option value="cm">cm</option>
                        <option value="inch">inch</option>
                      </select>
                    </div>
                  </div>
                </div>

                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            )}
          </div>

          {showResults && results && (
            <div style={resultsCardStyle}>
              <h3 style={h2Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <span style={resultLabelStyle}>{t('estimatedGfr')}</span>
                <span style={resultValueStyle}>{results.mainGfr}</span>
                <span style={resultUnitStyle}>mL/min/1.73m²</span>
              </div>

              <div style={ckdStageStyle(results.stage.class)}>
                <h4 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: colors.textPrimary }}>
                  {results.stage.title}
                </h4>
                <p style={{ color: '#475569', margin: 0, lineHeight: 1.6 }}>{results.stage.description}</p>
              </div>

              <div style={resultGridStyle}>
                {results.ckdEpi && (
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('ckdEpiEquation')}</span>
                    <span style={{ ...resultValueStyle, fontSize: '1.25rem' }}>{results.ckdEpi} mL/min/1.73m²</span>
                  </div>
                )}
                {results.mdrd && (
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('mdrdEquation')}</span>
                    <span style={{ ...resultValueStyle, fontSize: '1.25rem' }}>{results.mdrd} mL/min/1.73m²</span>
                  </div>
                )}
                {results.mayo && (
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('mayoQuadratic')}</span>
                    <span style={{ ...resultValueStyle, fontSize: '1.25rem' }}>{results.mayo} mL/min/1.73m²</span>
                  </div>
                )}
                {results.schwartz && (
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('schwartzFormula')}</span>
                    <span style={{ ...resultValueStyle, fontSize: '1.25rem' }}>{results.schwartz} mL/min/1.73m²</span>
                  </div>
                )}
              </div>

              <div style={infoBoxStyle}>
                <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <strong style={{ color: colors.textPrimary }}>{t('noteLabel')}</strong> {t('noteText')}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight5')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight6')}</li>
        </ul>
      </section>

      {/* Understanding GFR */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('understandingTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: '1.3rem', marginTop: '1.5rem' }}>{t('whatIsGfrTitle')}</h3>
        <p style={paragraphStyle}>{t('whatIsGfrText')}</p>

        <h3 style={{ ...h2Style, fontSize: '1.3rem', marginTop: '1.5rem' }}>{t('equationsTitle')}</h3>
        <p style={paragraphStyle}>{t('equationsIntro')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('ckdEpiName')}</strong> {t('ckdEpiDesc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('mdrdName')}</strong> {t('mdrdDesc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('mayoName')}</strong> {t('mayoDesc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('schwartzName')}</strong> {t('schwartzDesc')}
          </li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: '1.3rem', marginTop: '1.5rem' }}>{t('ckdStagesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('stage1Name')}</strong> {t('stage1Desc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('stage2Name')}</strong> {t('stage2Desc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('stage3aName')}</strong> {t('stage3aDesc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('stage3bName')}</strong> {t('stage3bDesc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('stage4Name')}</strong> {t('stage4Desc')}
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <strong>{t('stage5Name')}</strong> {t('stage5Desc')}
          </li>
        </ul>
      </section>

      {/* Clinical Applications */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('clinicalTitle')}</h2>
        <p style={paragraphStyle}>{t('clinicalIntro')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('clinical1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('clinical2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('clinical3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('clinical4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('clinical5')}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice5')}</li>
        </ul>
      </section>

      {/* Limitations */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('limitationsTitle')}</h2>
        <p style={paragraphStyle}>{t('limitationsIntro')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('limitation1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('limitation2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('limitation3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('limitation4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('limitation5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq7Question')}</h3>
          <p style={paragraphStyle}>{t('faq7Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={referenceStyle}>
          1. UPMC - <a href="https://www.upmc.com/services/transplant/kidney-pancreas/glomerular-filtration-rate-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('ref1')}</a>
        </p>
        <p style={referenceStyle}>
          2. NIDDK - <a href="https://www.niddk.nih.gov/health-information/professionals/clinical-tools-patient-management/kidney-disease/laboratory-evaluation/estimated-gfr-calculators" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('ref2')}</a>
        </p>
        <p style={referenceStyle}>
          3. Cleveland Clinic - <a href="https://my.clevelandclinic.org/health/diagnostics/21593-estimated-glomerular-filtration-rate-egfr" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('ref3')}</a>
        </p>
        <p style={referenceStyle}>
          4. National Kidney Foundation - <a href="https://www.kidney.org/professionals/gfr_calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('ref4')}</a>
        </p>
        <p style={referenceStyle}>
          5. National Kidney Foundation - <a href="https://www.kidney.org/kidney-topics/estimated-glomerular-filtration-rate-egfr" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('ref5')}</a>
        </p>
        <p style={referenceStyle}>
          6. DaVita - <a href="https://davita.com/gfr-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>{t('ref6')}</a>
        </p>
      </section>
    </div>
  );
}